import { useState, useRef, useEffect } from 'react';
import { useApp } from '../../context/AppContext';
import { useNavigate, Link } from 'react-router-dom';
import { auth, db } from '../../firebase';
import { collection, query, where, getDocs, addDoc, doc, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { predefinedIcons } from '../../constants/menuIcons';
import { FaWhatsapp, FaCopy, FaCheck } from 'react-icons/fa';

function Step6() {
  const { state, dispatch } = useApp();
  const { appData, totalPrice, priceBreakdown } = state;
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [iframeKey, setIframeKey] = useState(0);
  const [iframeError, setIframeError] = useState(false);
  const [saving, setSaving] = useState(false);
  const iframeRef = useRef(null);
  const [objectUrls, setObjectUrls] = useState({ logo: null, splash: null });
  const [showWhatsAppModal, setShowWhatsAppModal] = useState(false);
  const [appId, setAppId] = useState(null);
  const [copiedAppId, setCopiedAppId] = useState(false);
  const [couponCode, setCouponCode] = useState('');
  const [discountApplied, setDiscountApplied] = useState(0);
  const [isApplyingCoupon, setIsApplyingCoupon] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);

  // Novos estados para tratamento avançado do iframe
  const [errorDetails, setErrorDetails] = useState('');
  const [useHttps, setUseHttps] = useState(true);
  const [retryCount, setRetryCount] = useState(0);

  // Função para formatar a URL corretamente com tratamento de protocolo
  const getSiteUrl = () => {
    if (!appData.url) return 'https://exemplo.com';
    
    let url = appData.url;
    // Remove protocolos existentes
    url = url.replace(/^https?:\/\//, '');
    
    // Força HTTPS por padrão, mas permite fallback para HTTP
    if (useHttps) {
      return `https://${url}`;
    } else {
      return `http://${url}`;
    }
  };

  
  // Coupon auto-fill logic
  useEffect(() => {
    const activeCoupon = localStorage.getItem('activeCoupon');
    if (activeCoupon) {
      setCouponCode(activeCoupon);
    }
  }, []);

  const applyCoupon = async () => {
    if (!couponCode) return;
    setIsApplyingCoupon(true);
    setCouponError('');
    try {
      const q = query(collection(db, 'coupons'), where('code', '==', couponCode), where('active', '==', true));
      const querySnapshot = await getDocs(q);
      
      if (!querySnapshot.empty) {
        const couponData = querySnapshot.docs[0].data();
        setDiscountApplied(couponData.discountPercentage || 10);
      } else {
        setCouponError('Cupom inválido ou expirado.');
        setDiscountApplied(0);
      }
    } catch (err) {
      setCouponError('Erro ao validar cupom.');
      setDiscountApplied(0);
    } finally {
      setIsApplyingCoupon(false);
    }
  };

  const discountAmount = discountApplied ? (totalPrice * (discountApplied / 100)) : 0;
  const finalPrice = totalPrice - discountAmount;

  const siteUrl = getSiteUrl();

  // Função para finalizar e resetar todos os dados
  const handleFinalize = async () => {
    try {
      // 1️⃣ Primeiro redirecionar
      navigate('/dashboard', {
        state: {
          message: 'Seu aplicativo está sendo processado. O status é: Ops! Você tem um pagamento em aberto 😉',
          appId: appId,
          status: 'Ops! Você tem um pagamento em aberto 😉'
        },
        replace: true
      });

      // 2️⃣ Depois resetar o contexto (com pequeno delay para garantir o redirecionamento)
      setTimeout(() => {
        dispatch({ type: 'RESET_APP_DATA' });

        // 3️⃣ Limpar localStorage
        localStorage.removeItem('step1');
        localStorage.removeItem('step2');
        localStorage.removeItem('step3');
        localStorage.removeItem('step4');
      }, 100);

    } catch (error) {
      console.error('Erro ao finalizar compra:', error);
    }
  };

  const handleFinalizeOrder = async () => {
    if (!acceptedTerms) {
      alert('Por favor, confirme que leu e aceita o Contrato de Prestação de Serviços e os Termos de Uso marcando a caixa de seleção.');
      return;
    }
    setSaving(true);
    try {
      const user = auth.currentUser;
      if (!user) throw new Error('Usuário não autenticado');

      let logoUrl = appData.logo instanceof File ? '' : appData.logo;
      let splashUrl = appData.splash instanceof File ? '' : appData.splash;

      // Limpar ícones para garantir que apenas dados primitivos (id, name, url, price) sejam salvos,
      // removendo referências a componentes React (funções) presentes na definição de menuIcons.
      const cleanIcons = Array.isArray(appData.icons)
        ? appData.icons.map(icon => ({
            id: icon.id,
            name: icon.name,
            url: icon.url || '',
            price: Number(icon.price) || 0
          }))
        : [];

      const appConfig = {
        name: appData.name || 'Meu App',
        url: appData.url || '',
        logo: logoUrl || '',
        splash: splashUrl || '',
        loaderColor: appData.loaderColor || '#000000',
        platforms: appData.platforms || ['android'],
        hasMenu: !!appData.hasMenu,
        menuColor: appData.menuColor || '#ffffff',
        icons: cleanIcons,
        category: appData.category || 'Geral',
        finalPrice: finalPrice,
      };

      let createdAppId = null;

      // Captura de evidências de aceite jurídico
      let userIp = 'Não identificado';
      try {
        const ipRes = await Promise.race([
          fetch('https://api.ipify.org?format=json').then(r => r.json()),
          new Promise((_, reject) => setTimeout(() => reject(new Error('timeout')), 1500))
        ]);
        if (ipRes?.ip) userIp = ipRes.ip;
      } catch (e) {
        console.log('IP lookup fallback:', e?.message);
      }

      const clientUserAgent = typeof navigator !== 'undefined' ? navigator.userAgent : 'Navegador Web';
      const acceptanceTimestamp = new Date().toISOString();
      const acceptanceHash = `ACEITE-${user.uid.substring(0, 6).toUpperCase()}-${Date.now().toString(36).toUpperCase()}`;

      const legalAcceptanceData = {
        accepted: true,
        acceptedAt: acceptanceTimestamp,
        userId: user.uid,
        userEmail: user.email || '',
        userName: user.displayName || user.email || '',
        ipAddress: userIp,
        userAgent: clientUserAgent,
        contractVersion: '1.0-2025',
        fidelityMonths: 12,
        documentHash: acceptanceHash,
        contractTitle: 'Instrumento Particular de Contrato de Prestação de Serviços Tecnológicos e Licenciamento de Software (SaaS)',
      };

      // 1. Tentar salvar pela API Node se estiver respondendo
      try {
        const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:3001';
        const response = await fetch(`${apiUrl}/processCheckout`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            appConfig: {
              ...appConfig,
              legalAcceptance: legalAcceptanceData,
              acceptedTerms: true,
              acceptedContract: true,
            },
            couponData: discountApplied > 0 ? { code: couponCode, discount: discountApplied } : null,
            userAuth: {
              uid: user.uid,
              email: user.email
            }
          })
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.appId) {
            createdAppId = data.appId;
          }
        }
      } catch (apiError) {
        console.warn('API backend offline ou inacessível. Salvando diretamente no Firestore:', apiError);
      }

      // 2. Se a API estiver offline/inacessível, salvar diretamente no Firestore
      if (!createdAppId) {
        // Função auxiliar para garantir remoção de qualquer função ou tipo incompatível
        const sanitizedConfig = JSON.parse(JSON.stringify(appConfig, (key, value) => {
          if (typeof value === 'function') return undefined;
          return value;
        }));

        const appDataToSave = {
          ...sanitizedConfig,
          userId: user.uid,
          userEmail: user.email,
          status: 'pending_payment',
          paymentStatus: 'pending',
          totalPrice: finalPrice,
          couponDiscount: discountApplied > 0 ? Number(discountApplied) : 0,
          couponCode: discountApplied > 0 ? couponCode : null,
          saasPlan: 'mensal',
          saasStatus: 'pending',
          paymentMethod: 'whatsapp',
          acceptedTerms: true,
          acceptedContract: true,
          termsAcceptedAt: serverTimestamp(),
          legalAcceptance: legalAcceptanceData,
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
        };

        const docRef = await addDoc(collection(db, 'apps'), appDataToSave);
        createdAppId = docRef.id;
      }

      // 3. Garantir registro de auditoria jurídica e atualizar perfil do usuário
      if (createdAppId) {
        try {
          await setDoc(doc(db, 'apps', createdAppId), {
            acceptedTerms: true,
            acceptedContract: true,
            termsAcceptedAt: serverTimestamp(),
            legalAcceptance: legalAcceptanceData,
          }, { merge: true });
        } catch (appMergeErr) {
          console.warn('Erro ao mesclar legalAcceptance no app:', appMergeErr);
        }

        try {
          await updateDoc(doc(db, 'users', user.uid), {
            acceptedTerms: true,
            acceptedContract: true,
            termsAcceptedAt: serverTimestamp(),
            lastContractAppId: createdAppId,
            lastContractAcceptedAt: serverTimestamp()
          });
        } catch (userUpdateErr) {
          console.warn('Erro ao atualizar aceite no perfil do usuário:', userUpdateErr);
        }

        try {
          await addDoc(collection(db, 'contractAcceptances'), {
            appId: createdAppId,
            appName: appData.name || 'Meu App',
            finalPrice: finalPrice,
            platforms: appData.platforms || ['android'],
            siteUrl: siteUrl || '',
            ...legalAcceptanceData,
            createdAt: serverTimestamp()
          });
        } catch (auditErr) {
          console.warn('Erro ao salvar em contractAcceptances:', auditErr);
        }
      }

      setAppId(createdAppId);
      setShowWhatsAppModal(true);

    } catch (error) {
      console.error('Erro ao finalizar pedido:', error);
      alert(`Erro: ${error.message}`);
    } finally {
      setSaving(false);
    }
  };

  const handleGoToDashboard = () => {
    handleFinalize();
  };

  const handleBack = () => {
    dispatch({ type: 'PREV_STEP' });
  };

  // Função para recarregar o iframe
  const reloadIframe = () => {
    setIframeKey(prev => prev + 1);
    setIsLoading(true);
    setIframeError(false);
    setErrorDetails('');
    setRetryCount(prev => prev + 1);
  };

  // Função para alternar entre HTTPS e HTTP
  const toggleProtocol = () => {
    setUseHttps(!useHttps);
    setRetryCount(0);
    reloadIframe();
  };

  // Função para tentar automaticamente com HTTP se HTTPS falhar
  const handleIframeError = () => {
    setIsLoading(false);
    setIframeError(true);
    setErrorDetails('Conexão recusada pelo site. Tentando solução alternativa...');
    
    // Tentar automaticamente sem HTTPS após 2 segundos se ainda não tentou
    if (useHttps && retryCount < 2) {
      setTimeout(() => {
        setUseHttps(false);
        reloadIframe();
      }, 2000);
    }
  };

  // Criar object URLs apenas uma vez e limpar corretamente
  useEffect(() => {
    const newObjectUrls = {};

    if (appData.logo instanceof File) {
      newObjectUrls.logo = URL.createObjectURL(appData.logo);
    }

    if (appData.splash instanceof File) {
      newObjectUrls.splash = URL.createObjectURL(appData.splash);
    }

    setObjectUrls(newObjectUrls);

    return () => {
      if (newObjectUrls.logo) URL.revokeObjectURL(newObjectUrls.logo);
      if (newObjectUrls.splash) URL.revokeObjectURL(newObjectUrls.splash);
    };
  }, [appData.logo, appData.splash]);

  // Debug avançado para iframe
  useEffect(() => {
    console.log('🔍 Iframe Debug Info:', {
      siteUrl,
      useHttps,
      iframeKey,
      isLoading,
      iframeError,
      errorDetails,
      retryCount
    });
  }, [siteUrl, iframeKey, isLoading, iframeError, errorDetails, retryCount, useHttps]);

  // Componente com ícones PNG
  const CardIcons = () => (
    <div className="flex justify-center space-x-4 mt-4">
      <img
        src="https://logodownload.org/wp-content/uploads/2014/07/mastercard-logo-8.png"
        width="40"
        height="24"
        alt="Mastercard"
        className="object-contain"
      />
      <img
        src="https://www.pikpng.com/pngl/b/80-809937_visa-png-transparent-background-visa-logo-clipart.png"
        width="40"
        height="24"
        alt="Visa"
        className="object-contain"
      />
    </div>
  );

  return (
    <div className="flex flex-col xl:flex-row gap-6 lg:gap-8 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto w-full">
      {/* Modal de progresso ao salvar */}
      {saving && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
          <div className="bg-white p-8 rounded-2xl shadow-2xl max-w-md w-full mx-auto text-center">
            <h3 className="text-xl font-bold mb-3 text-gray-900">
              Salvando seu Aplicativo...
            </h3>
            <p className="text-sm text-gray-600 mb-6">
              Estamos registrando todas as configurações do seu app.
            </p>
            <div className="flex justify-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gray-900"></div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Sucesso com Redirecionamento para WhatsApp */}
      {showWhatsAppModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4 backdrop-blur-sm overflow-y-auto animate-fade-in">
          <div className="bg-white p-6 sm:p-8 rounded-2xl shadow-2xl max-w-lg w-full mx-auto relative animate-slide-up border border-gray-100">
            <div className="text-center mb-6">
              <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-full bg-green-100 mb-4 text-green-600">
                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-gray-900 mb-2">
                Aplicativo Criado com Sucesso!
              </h3>
              <p className="text-gray-600 text-sm">
                Seu aplicativo foi registrado no sistema. Conclua o pagamento via WhatsApp para darmos início ao desenvolvimento.
              </p>
            </div>

            {/* Resumo do Pedido */}
            <div className="bg-gray-50 rounded-xl p-4 sm:p-5 mb-6 border border-gray-100 space-y-3">
              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-sm text-gray-500">Nome do App:</span>
                <span className="text-sm font-bold text-gray-900">{appData.name || 'Meu App'}</span>
              </div>

              <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                <span className="text-sm text-gray-500">ID do Pedido:</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-mono font-medium text-gray-700 bg-white px-2 py-0.5 rounded border border-gray-200 max-w-[150px] truncate">
                    {appId}
                  </span>
                  <button
                    onClick={() => {
                      if (appId) {
                        navigator.clipboard.writeText(appId);
                        setCopiedAppId(true);
                        setTimeout(() => setCopiedAppId(false), 2000);
                      }
                    }}
                    className="text-xs text-blue-600 hover:text-blue-800 p-1"
                    title="Copiar ID"
                  >
                    {copiedAppId ? <FaCheck className="text-green-500" /> : <FaCopy />}
                  </button>
                </div>
              </div>

              <div className="flex justify-between items-center pt-1">
                <span className="text-sm font-medium text-gray-700">Primeira Mensalidade:</span>
                <span className="text-xl font-extrabold text-gray-900">
                  R$ {finalPrice.toFixed(2)}
                </span>
              </div>
            </div>

            {/* Aviso WhatsApp */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 mb-6 text-sm text-green-900">
              <p className="font-semibold flex items-center gap-2 mb-1">
                <FaWhatsapp className="text-green-600 text-lg flex-shrink-0" />
                Ativação Imediata via WhatsApp
              </p>
              <p className="text-xs text-green-800 leading-relaxed">
                Clique no botão abaixo para enviar os dados do seu pedido para nossa equipe. Nós forneceremos as opções de pagamento (PIX / Cartão) e iniciaremos o seu app!
              </p>
            </div>

            {/* Botões de Ação */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  const user = auth.currentUser;
                  const msg = `Olá! Acabei de cadastrar meu aplicativo *${appData.name || 'Meu App'}* na CriaApp!

*Detalhes do Pedido:*
- *ID do App:* ${appId}
- *URL:* ${siteUrl || 'Não informado'}
- *Plataformas:* ${appData.platforms?.join(', ') || 'Android'}
- *Valor:* R$ ${finalPrice.toFixed(2)}
- *Cliente:* ${user?.displayName || user?.email || 'Cliente'}

Gostaria de efetuar o pagamento para iniciar a produção do aplicativo!`;
                  window.open(`https://api.whatsapp.com/send?phone=5582996992182&text=${encodeURIComponent(msg)}`, '_blank');
                }}
                className="w-full py-3.5 px-4 bg-[#25D366] hover:bg-[#20bd5a] text-white font-bold rounded-xl shadow-md hover:shadow-lg transition-all flex items-center justify-center gap-2 text-base active:scale-[0.99]"
              >
                <FaWhatsapp className="text-xl" />
                <span>Enviar Mensagem no WhatsApp</span>
              </button>

              <button
                onClick={handleGoToDashboard}
                className="w-full py-3 px-4 bg-gray-900 hover:bg-black text-white font-semibold rounded-xl transition-colors text-sm shadow-sm"
              >
                Ir para o Dashboard
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resumo e informações - Lado esquerdo */}
      <div className="flex-1 w-full">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">Resumo do Aplicativo</h2>
          <p className="text-gray-600 text-lg">Revise todas as informações antes de finalizar</p>
        </div>

        <div className="space-y-8">
          {/* Informações Básicas */}
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-xl font-bold mb-6 text-gray-900 border-b border-gray-100 pb-4">Informações Básicas</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Nome do App</p>
                <p className="font-semibold text-lg text-gray-900 mt-1">{appData.name || 'Não definido'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">URL do Site</p>
                <p
                  title={siteUrl}
                  className="font-semibold text-lg text-gray-900 mt-1 break-all"
                >
                  {siteUrl}
                </p>
              </div>

              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Categoria</p>
                <p className="font-semibold text-lg text-gray-900 mt-1">{appData.category || 'Geral'}</p>
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Status Inicial</p>
                <p className="font-semibold text-lg text-orange-600 mt-1">Pendente</p>
              </div>
            </div>
          </div>

          {/* Design */}
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-xl font-bold mb-6 text-gray-900 border-b border-gray-100 pb-4">Design</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Logo</p>
                {appData.logo ? (
                  <img
                    src={objectUrls.logo || appData.logo}
                    alt="Logo"
                    className="h-16 w-16 object-contain mt-2 rounded-lg border border-gray-100 shadow-sm"
                  />
                ) : (
                  <p className="font-medium text-gray-400 text-lg mt-1">Não definido</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Splash Screen</p>
                {appData.splash ? (
                  <img
                    src={objectUrls.splash || appData.splash}
                    alt="Splash"
                    className="h-16 w-16 object-contain mt-2 rounded-lg border border-gray-100 shadow-sm"
                  />
                ) : (
                  <p className="font-medium text-gray-400 text-lg mt-1">Não definido</p>
                )}
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Cor do Loader</p>
                <div className="flex items-center mt-2">
                  <div
                    className="w-8 h-8 rounded-full border border-gray-200 mr-3 flex-shrink-0 shadow-sm"
                    style={{ backgroundColor: appData.loaderColor }}
                  ></div>
                  <span className="font-semibold text-lg text-gray-900 break-all">{appData.loaderColor}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Plataformas */}
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-xl font-bold mb-6 text-gray-900 border-b border-gray-100 pb-4">Plataformas</h3>
            <div className="flex flex-wrap gap-3">
              {appData.platforms && appData.platforms.length > 0 ? (
                appData.platforms.map(platform => (
                  <span 
                    key={platform} 
                    className="bg-gray-900 text-white px-4 py-2 rounded-xl text-sm font-bold shadow-md"
                  >
                    {platform === 'android' ? 'Android' : 'iOS'}
                  </span>
                ))
              ) : (
                <p className="text-gray-400 text-lg">Nenhuma plataforma selecionada</p>
              )}
            </div>
          </div>

          {/* Menu e Ícones */}
          {appData.hasMenu && (
            <div className="bg-white p-8 rounded-2xl shadow-xl">
              <h3 className="text-xl font-bold mb-6 text-gray-900 border-b border-gray-100 pb-4">Menu e Ícones</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Cor do Menu</p>
                  <div className="flex items-center mt-2">
                    <div
                      className="w-8 h-8 rounded-full border border-gray-200 mr-3 flex-shrink-0 shadow-sm"
                      style={{ backgroundColor: appData.menuColor }}
                    ></div>
                    <span className="font-semibold text-lg text-gray-900 break-all">{appData.menuColor}</span>
                  </div>
                </div>
                <div>
                  <p className="text-sm font-medium text-gray-500 uppercase tracking-wide">Ícones Selecionados</p>
                  <p className="font-semibold text-lg text-gray-900 mt-1">{appData.icons?.length || 0} ícones</p>
                </div>
              </div>
            </div>
          )}

          {/* Resumo Financeiro */}
          <div className="bg-white p-8 rounded-2xl shadow-xl">
            <h3 className="text-xl font-bold mb-6 text-gray-900 border-b border-gray-100 pb-4">Resumo Financeiro</h3>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Aplicação básica:</span>
                <span className="font-bold text-gray-900">R$ {priceBreakdown.base?.toFixed(2) || '0,00'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Logo e Splash:</span>
                <span className="font-bold text-gray-900">R$ {priceBreakdown.logo?.toFixed(2) || '0,00'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Personalização do loader:</span>
                <span className="font-bold text-gray-900">R$ {priceBreakdown.loader?.toFixed(2) || '0,00'}</span>
              </div>

              <div className="flex justify-between items-center">
                <span className="text-gray-600 font-medium">Plataformas:</span>
                <span className="font-bold text-gray-900">R$ {priceBreakdown.platforms?.toFixed(2) || '0,00'}</span>
              </div>

              {appData.hasMenu && (
                <>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600 font-medium">Menu de navegação:</span>
                    <span className="font-bold text-gray-900">R$ {priceBreakdown.menu?.toFixed(2) || '0,00'}</span>
                  </div>

                  {priceBreakdown.icons > 0 && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600 font-medium">{appData.icons?.length} ícones:</span>
                      <span className="font-bold text-gray-900">R$ {priceBreakdown.icons?.toFixed(2) || '0,00'}</span>
                    </div>
                  )}
                </>
              )}


              <div className="border-t border-gray-100 pt-4 mt-4">
                {discountApplied > 0 && (
                  <div className="flex justify-between items-center text-green-600 mb-2">
                    <span className="text-lg font-medium">Desconto ({discountApplied}%):</span>
                    <span className="text-lg font-bold">- R$ {discountAmount.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between items-center">
                  <span className="text-xl font-bold text-gray-900">Mensalidade:</span>
                  <span className="text-2xl font-bold text-gray-900">R$ {finalPrice.toFixed(2)}/mês</span>
                </div>
              </div>

            {/* UI do Cupom */}
            <div className="mt-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
              <label className="block text-sm font-bold text-gray-700 mb-2">Possui um cupom de desconto?</label>
              <div className="flex gap-2">
                <input 
                  type="text" 
                  value={couponCode} 
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Ex: PROMO10-XXXX"
                  className="flex-1 px-4 py-3 bg-white rounded-xl border border-gray-200 text-gray-900 font-bold placeholder-gray-400 focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10 outline-none uppercase transition-all shadow-sm"
                  disabled={discountApplied > 0}
                />
                <button 
                  onClick={applyCoupon}
                  disabled={isApplyingCoupon || !couponCode || discountApplied > 0}
                  className="px-4 py-2 bg-gray-900 text-white font-bold rounded-lg hover:bg-black disabled:opacity-50 transition-all"
                >
                  {isApplyingCoupon ? 'Validando...' : discountApplied > 0 ? 'Aplicado' : 'Aplicar'}
                </button>
              </div>
              {couponError && <p className="text-red-500 text-sm mt-2">{couponError}</p>}
              {discountApplied > 0 && <p className="text-green-600 text-sm mt-2 font-medium">Cupom aplicado com sucesso!</p>}
            </div>

            </div>

            {/* Informações de pagamento */}
            <div className="mt-8 p-6 bg-gray-50 rounded-xl border border-gray-100">
              <h4 className="font-bold text-gray-900 mb-4 text-center text-lg">Formas de Pagamento</h4>
              <CardIcons />
              <p className="text-sm text-gray-500 mt-4 text-center">
                Após finalizar seu pedido, você poderá confirmar o pagamento via WhatsApp de forma ágil e segura.
              </p>
              <div className="mt-4 flex items-center justify-center space-x-2">
                <div className="h-2 w-2 rounded-full bg-orange-500"></div>
                <p className="text-sm font-medium text-gray-900">
                  Status inicial: <span className="text-orange-600">Pendente de Pagamento</span>
                </p>
              </div>

              {/* Termos e Contrato com Checkbox de Aceite */}
              <div className="mt-4 pt-4 border-t border-gray-200">
                <label className="flex items-start gap-3 cursor-pointer select-none text-left p-3 rounded-xl bg-white border border-gray-200 hover:border-blue-400 transition-all shadow-sm">
                  <input
                    type="checkbox"
                    id="acceptTerms"
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    className="mt-0.5 h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer flex-shrink-0"
                  />
                  <span className="text-xs text-gray-700 leading-relaxed">
                    Li e aceito integralmente o{' '}
                    <Link
                      to="/contrato"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Contrato de Prestação de Serviços
                    </Link>{' '}
                    (vigência mínima de 12 meses) e os{' '}
                    <Link
                      to="/termos-de-uso"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 hover:underline font-semibold"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Termos de Uso
                    </Link>.
                  </span>
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* Botões de navegação */}
        <div className="mt-6 lg:mt-8 flex flex-col sm:flex-row gap-3 justify-between">
          <button
            onClick={handleBack}
            disabled={saving}
            className="
              w-full
              sm:w-auto
              px-4 
              py-3 
              bg-gray-300 
              text-gray-700 
              rounded-lg 
              hover:bg-gray-400 
              disabled:opacity-50 
              disabled:cursor-not-allowed
              flex 
              items-center 
              justify-center
              transition-colors
              duration-200
              min-h-[44px]
              text-base
              font-medium
              shadow-sm
            "
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M9.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 1.414L7.414 9H15a1 1 0 110 2H7.414l2.293 2.293a1 1 0 010 1.414z" clipRule="evenodd" />
            </svg>
            Voltar
          </button>
          <button
            onClick={handleFinalizeOrder}
            disabled={saving || !acceptedTerms}
            className={`
              w-full
              sm:w-auto
              px-6 
              py-3 
              rounded-lg 
              flex 
              items-center 
              justify-center
              transition-all
              duration-200
              ease-in-out
              min-h-[44px]
              text-base
              font-medium
              ${saving || !acceptedTerms
                ? 'bg-gray-200 text-gray-400 cursor-not-allowed shadow-none border border-gray-200'
                : 'bg-blue-600 hover:bg-blue-700 text-white shadow-md hover:shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 active:scale-[0.99]'}
            `}
            title={!acceptedTerms ? 'Marque o aceite dos termos acima para prosseguir' : 'Finalizar e salvar pedido'}
          >
            {saving ? (
              <>
                <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                <span className="whitespace-nowrap">Salvando aplicativo...</span>
              </>
            ) : (
              <>
                <span className="whitespace-nowrap">Finalizar Pedido</span>
                <svg 
                  xmlns="http://www.w3.org/2000/svg" 
                  className="h-5 w-5 ml-2 flex-shrink-0" 
                  viewBox="0 0 20 20" 
                  fill="currentColor"
                >
                  <path 
                    fillRule="evenodd" 
                    d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" 
                    clipRule="evenodd" 
                  />
                </svg>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Preview do aplicativo - Lado direito */}
      <div className="xl:w-[400px] lg:w-full flex-shrink-0">
        <div className="sticky top-6 lg:top-8">
          <h3 className="text-lg lg:text-xl font-semibold mb-4 text-gray-800 text-center lg:text-left">
            Preview do App
          </h3>

          <div className="flex justify-center lg:justify-start">
            <div className="relative" style={{ 
              width: 'min(100%, 400px)', 
              height: 'min(700px, 90vh)',
              maxWidth: '400px'
            }}>
              {/* Moldura do celular */}
              <div className="absolute inset-0 bg-gray-800 rounded-3xl p-3 shadow-2xl">
                <div className="h-full bg-white rounded-2xl overflow-hidden flex flex-col">

                  {/* Status Bar (como em um celular real) */}
                  <div className="bg-gray-900 text-white px-4 py-1 flex justify-between items-center text-xs">
                    <span>9:41</span>
                    <div className="flex items-center space-x-2">
                      <span>📶</span>
                      <span>📡</span>
                      <span>🔋</span>
                    </div>
                  </div>

                  {/* WebView - Site carregado dentro do app usando iframe */}
                  <div className="flex-1 relative overflow-hidden">
                    {isLoading && (
                      // Tela de carregamento
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 p-4">
                        {appData.logo ? (
                          <img
                            src={objectUrls.logo || appData.logo}
                            alt="App Logo"
                            className="h-16 w-16 object-contain mb-4"
                          />
                        ) : (
                          <div className="h-16 w-16 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                            <span className="text-blue-600 font-bold text-lg">A</span>
                          </div>
                        )}

                        <div
                          className="w-8 h-8 rounded-full animate-spin"
                          style={{
                            border: `4px solid ${appData.loaderColor || '#3B82F6'}`,
                            borderTop: '4px solid transparent'
                          }}
                        ></div>

                        <p className="text-gray-500 text-sm text-center">Carregando {siteUrl}</p>
                        <p className="text-gray-400 text-xs mt-2 text-center">
                          Protocolo: {useHttps ? 'HTTPS' : 'HTTP'}
                        </p>
                      </div>
                    )}

                    {iframeError && (
                      <div className="absolute inset-0 flex flex-col items-center justify-center bg-white z-10 p-4">
                        <div className="text-red-500 text-4xl mb-4">⚠️</div>
                        <p className="text-gray-700 text-center mb-2">Não foi possível carregar o site</p>
                        <p className="text-gray-500 text-sm text-center mb-4">
                          {errorDetails || 'O site pode ter restrições de segurança que impedem a visualização em iframe.'}
                        </p>
                        
                        <div className="flex flex-col gap-2 w-full max-w-xs">
                          <button
                            onClick={reloadIframe}
                            className="bg-blue-600 text-white py-2 px-4 rounded text-sm hover:bg-blue-700 transition-colors"
                          >
                            Tentar Novamente
                          </button>
                          
                          <button
                            onClick={toggleProtocol}
                            className="bg-gray-600 text-white py-2 px-4 rounded text-sm hover:bg-gray-700 transition-colors"
                          >
                            Usar {useHttps ? 'HTTP' : 'HTTPS'}
                          </button>
                          
                          <a
                            href={siteUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 text-sm underline text-center py-2"
                          >
                            Abrir site em nova janela
                          </a>
                        </div>
                      </div>
                    )}

                    {/* Iframe carregando o site real - CONFIGURAÇÃO MELHORADA */}
                    <iframe
                      key={iframeKey}
                      ref={iframeRef}
                      src={siteUrl}
                      title="Preview do site"
                      className="w-full h-full border-0"
                      onLoad={() => {
                        setIsLoading(false);
                        setIframeError(false);
                        setErrorDetails('');
                        setRetryCount(0);
                      }}
                      onError={handleIframeError}
                      sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-modals"
                      allow="accelerometer; camera; encrypted-media; geolocation; gyroscope; microphone; midi; payment"
                      referrerPolicy="no-referrer-when-downgrade"
                      style={{
                        overflow: 'hidden'
                      }}
                    />
                  </div>

                  {/* Menu Button (na parte inferior) */}
                  {appData.hasMenu && (
                    <div
                      className="border-t border-gray-200"
                      style={{ backgroundColor: appData.menuColor || '#3B82F6' }}
                    >
                      <div className="flex justify-around items-center p-2">
                        {appData.icons && appData.icons.slice(0, 5).map((iconObj, index) => {
                          const iconDef = predefinedIcons.find(pi => pi.id === iconObj.id);
                          const IconComponent = iconDef ? iconDef.icon : null;
                          return (
                            <div key={index} className="flex flex-col items-center p-1">
                              {IconComponent ? (
                                <IconComponent className="h-5 w-5 lg:h-6 lg:w-6 text-white" />
                              ) : iconObj.url ? (
                                <img 
                                  src={iconObj.url} 
                                  alt={iconObj.name} 
                                  className="h-5 w-5 lg:h-6 lg:w-6 object-contain filter brightness-0 invert" 
                                />
                              ) : null}
                              <span className="text-[10px] text-white mt-1 truncate max-w-[50px]">{iconObj.name}</span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="mt-4 text-center lg:text-left">
            <p className="text-sm text-gray-500 break-all">Carregando: {siteUrl}</p>
            <p className="text-xs text-gray-400 mt-1">
              {appData.url ? 'Site real carregado no app' : 'URL não definida - usando exemplo.com'}
              {useHttps ? ' (HTTPS)' : ' (HTTP)'}
            </p>
            {iframeError && (
              <p className="text-xs text-red-500 mt-1 text-center lg:text-left">
                {errorDetails || 'Erro ao carregar. Alguns sites não permitem visualização em iframe.'}
              </p>
            )}
            {retryCount > 0 && (
              <p className="text-xs text-orange-500 mt-1 text-center lg:text-left">
                Tentativas: {retryCount}
              </p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Step6;